import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

const HREmployeesModule = ({ onBack }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [hrPositions, setHrPositions] = useState([]);


  React.useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/hr/positions`);
        setHrPositions(res.data || []);
      } catch (e) {
        console.error('Error fetching HR positions:', e);
        setHrPositions([]);
      }
    };
    fetchPositions();
  }, []);

  React.useEffect(() => {
    fetchEmployees();
  }, [showActiveOnly]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const endpoint = showActiveOnly ? '/employees/active' : '/employees';
      const response = await axios.get(`${API}${endpoint}`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowEditModal(true);
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (window.confirm(`Are you sure you want to delete ${employeeName}?\n\nNote: If they have active job assignments, deletion will be prevented.`)) {
      try {
        const response = await axios.delete(`${API}/employees/${employeeId}`);
        alert(response.data.message || 'Employee deleted successfully!');
        fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        
        if (error.response?.status === 409) {
          // Employee has active assignments
          const message = error.response.data.detail;
          const forceDelete = window.confirm(
            `${message}\n\nWould you like to FORCE DELETE this employee and remove them from all assignments?\n\n⚠️ Warning: This will remove them from active jobs and may affect scheduling.`
          );
          
          if (forceDelete) {
            try {
              const forceResponse = await axios.delete(`${API}/employees/${employeeId}/force`);
              alert(forceResponse.data.message || 'Employee force deleted successfully!');
              fetchEmployees();
            } catch (forceError) {
              console.error('Error force deleting employee:', forceError);
              alert(`Error force deleting employee: ${forceError.response?.data?.detail || forceError.message}`);
            }
          }
        } else {
          alert(`Error deleting employee: ${error.response?.data?.detail || error.message}`);
        }
      }
    }
  };


  const positionLabel = (posId) => {
    if (!posId) return 'Crew Member';
    return hrPositions.find((p) => p.id === posId)?.title || posId;
  };

  const handleToggleActive = async (employee) => {
    try {
      const action = employee.active ? 'deactivate' : 'activate';
      // Use dedicated backend endpoints for activating/deactivating employees
      const url = employee.active


        ? `${API}/employees/${employee.id}/deactivate`
        : `${API}/employees/${employee.id}/activate`;

      await axios.put(url);
      alert(`Employee ${action}d successfully!`);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee status:', error);
      alert('Error updating employee status. Please try again.');
    }
  };


  const filteredEmployees = employees.filter(employee => 
    showActiveOnly ? employee.active : true
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium mb-4"
          >
            ← Back to Human Resources
          </button>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">👥 HR Employee Management</h1>
          <p className="text-gray-600">Complete employee records with HR-specific information</p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg transition-colors"
            >
              + ADD EMPLOYEE
            </button>
            <label className="flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="form-checkbox h-4 w-4 text-purple-600"
              />
              Show active only
            </label>
          </div>
          <div className="text-gray-600">
            {filteredEmployees.length} employees found
          </div>
        </div>

        {/* Employee List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading employees...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEmployees.map((employee) => (
              <div key={employee.id} className="bg-white rounded-xl p-6 shadow-lg border border-purple-100">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-gray-800">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        employee.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    
                    {/* Basic Information */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600">
                          <span className="font-semibold">💼 Position:</span> {positionLabel(employee.position)}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">🏢 Department:</span> {employee.department}
                        </p>
                        {employee.email && (
                          <p className="text-gray-600">
                            <span className="font-semibold">📧 Email:</span> {employee.email}
                          </p>
                        )}
                        {employee.phone && (
                          <p className="text-gray-600">
                            <span className="font-semibold">📞 Phone:</span> {employee.phone}
                          </p>
                        )}
                      </div>
                      <div>
                        {employee.hire_date && (
                          <p className="text-gray-600">
                            <span className="font-semibold">📅 Hire Date:</span> {employee.hire_date}
                          </p>
                        )}
                        {employee.emergency_contact_name && (
                          <p className="text-gray-600">
                            <span className="font-semibold">🚨 Emergency Contact:</span> {employee.emergency_contact_name}
                          </p>
                        )}
                        {employee.emergency_contact_phone && (
                          <p className="text-gray-600">
                            <span className="font-semibold">📞 Emergency Phone:</span> {employee.emergency_contact_phone}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* HR-Specific Information */}
                    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-bold text-purple-800 mb-2">🔐 HR Confidential Information</h4>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          {employee.drivers_license_number && (
                            <p className="text-gray-700">
                              <span className="font-semibold">🪪 License #:</span> {employee.drivers_license_number}
                            </p>
                          )}
                          {employee.drivers_license_class && (
                            <p className="text-gray-700">
                              <span className="font-semibold">🚗 License Class:</span> {employee.drivers_license_class}
                            </p>
                          )}
                          {employee.drivers_license_expiry && (
                            <p className="text-gray-700">
                              <span className="font-semibold">📅 License Expiry:</span> {employee.drivers_license_expiry}
                            </p>
                          )}
                        </div>
                        <div>
                          {typeof employee.benefits_enrolled === 'boolean' && (
                            <p className="text-gray-700">
                              <span className="font-semibold">🏥 Benefits:</span> {employee.benefits_enrolled ? 'Enrolled' : 'Not Enrolled'}
                            </p>
                          )}
                          {employee.benefits_details && (
                            <p className="text-gray-700">
                              <span className="font-semibold">📋 Benefits Details:</span> {employee.benefits_details}
                            </p>
                          )}
                          {employee.bank_account_info && (
                            <p className="text-gray-700">
                              <span className="font-semibold">🏦 Account Info:</span> {employee.bank_account_info}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 lg:w-32">
                    <button
                      onClick={() => handleEditEmployee(employee)}
                      className="bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg text-white font-bold text-sm shadow-sm transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(employee)}
                      className={`px-4 py-2 rounded-lg font-bold text-sm shadow-sm transition-colors ${
                        employee.active 
                          ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {employee.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteEmployee(employee.id, `${employee.first_name} ${employee.last_name}`)}
                      className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-white font-bold text-sm shadow-sm transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredEmployees.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No employees found</h3>
                <p className="text-gray-600">Add your first employee to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Add Employee Modal */}
        {showAddModal && (
          <HREmployeeModal
            employee={null}
            onClose={() => setShowAddModal(false)}
            onSave={() => {
              setShowAddModal(false);
              fetchEmployees();
            }}
          />
        )}

        {/* Edit Employee Modal */}
        {showEditModal && editingEmployee && (
          <HREmployeeModal
            employee={editingEmployee}
            onClose={() => {
              setShowEditModal(false);
              setEditingEmployee(null);
            }}
            onSave={() => {
              setShowEditModal(false);
              setEditingEmployee(null);
              fetchEmployees();
            }}
          />
        )}
        </div>
      </div>
  );
};

// HR Employee Modal Component with Additional Fields
const HREmployeeModal = ({ employee, onClose, onSave }) => {
  const [hrPositions, setHrPositions] = useState([]);
  const [manuals, setManuals] = useState([]);

  const [formData, setFormData] = useState({
    first_name: employee?.first_name || '',
    last_name: employee?.last_name || '',
    // Store employee.position as HR Position ID
    position: employee?.position || '',
    department: employee?.department || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    address: employee?.address || '',
    hire_date: employee?.hire_date || '',
    emergency_contact_name: employee?.emergency_contact_name || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
    notes: employee?.notes || '',
    active: employee?.active ?? true,
    // HR-specific fields
    drivers_license_number: employee?.drivers_license_number || '',
    drivers_license_class: employee?.drivers_license_class || '',
    drivers_license_expiry: employee?.drivers_license_expiry || '',
    bank_account_info: employee?.bank_account_info || '',
    benefits_enrolled: employee?.benefits_enrolled ?? null,
    benefits_details: employee?.benefits_details || ''
  });

  React.useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/hr/positions`);
        setHrPositions(res.data || []);
      } catch (e) {
        console.error('Error fetching HR positions:', e);
        setHrPositions([]);
      }
    };
    fetchPositions();
  }, []);

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (employee) {
        // Update existing employee
        await axios.put(`${API}/employees/${employee.id}`, formData);
        alert('Employee updated successfully!');
      } else {
        // Create new employee
        await axios.post(`${API}/employees`, formData);
        alert('Employee added successfully!');
      }
      onSave();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Error saving employee. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {employee ? 'Edit Employee (HR)' : 'Add New Employee (HR)'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">👤 Basic Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Position *
                </label>
                <select
                  required
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Select Position</option>
                  {hrPositions.map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Department *
                </label>
                <input
                  type="text"
                  required
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Hire Date
                </label>
                <input
                  type="date"
                  value={formData.hire_date}
                  onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">🚨 Emergency Contact</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* HR-Specific Fields */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="text-lg font-bold text-purple-800 mb-4">🔐 HR Confidential Information</h3>
            
            {/* Driver's License Information */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-700 mb-3">🪪 Driver's License Information</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={formData.drivers_license_number}
                    onChange={(e) => setFormData({...formData, drivers_license_number: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., D1234567890123"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    License Class
                  </label>
                  <select
                    value={formData.drivers_license_class}
                    onChange={(e) => setFormData({...formData, drivers_license_class: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select Class</option>
                    <option value="G">G - Full License</option>
                    <option value="G1">G1 - Learner's Permit</option>
                    <option value="G2">G2 - Probationary</option>
                    <option value="A">A - Motorcycle</option>
                    <option value="D">D - Commercial</option>
                    <option value="DZ">DZ - Commercial Truck</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={formData.drivers_license_expiry}
                    onChange={(e) => setFormData({...formData, drivers_license_expiry: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-700 mb-3">💰 Financial Information</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Bank Account Information
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_info}
                    onChange={(e) => setFormData({...formData, bank_account_info: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Institution, Transit, Account"
                  />
                </div>
              </div>
            </div>

            {/* Benefits Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-700 mb-3">🏥 Benefits Information</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Benefits Enrollment
                  </label>
                  <select
                    value={formData.benefits_enrolled === null ? '' : formData.benefits_enrolled.toString()}
                    onChange={(e) => setFormData({...formData, benefits_enrolled: e.target.value === '' ? null : e.target.value === 'true'})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Not Specified</option>
                    <option value="true">Enrolled</option>
                    <option value="false">Not Enrolled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Benefits Details
                  </label>
                  <input
                    type="text"
                    value={formData.benefits_details}
                    onChange={(e) => setFormData({...formData, benefits_details: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Plan details, coverage info, etc."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              rows="3"
              placeholder="Any additional notes or comments..."
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({...formData, active: e.target.checked})}
              className="form-checkbox h-4 w-4 text-purple-600"
            />
            <label htmlFor="active" className="text-gray-700 font-medium">
              Employee is active
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg font-bold transition-colors"
            >
              {saving ? 'Saving...' : (employee ? 'Update Employee' : 'Add Employee')}
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};



// ==================== HR POSITIONS MODULE ====================

const HRPositionsModule = ({ onBack }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [viewingPosition, setViewingPosition] = useState(null);
  const [viewPdfUrl, setViewPdfUrl] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const emptyForm = {
    id: null,
    title: '',
    job_description: '',
    duties: '',
    expectations: '',
    requirements: '',
    pay_type: 'hourly',
    pay_min: '',
    pay_max: ''
  };

  const [form, setForm] = useState(emptyForm);

  React.useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/positions`);
      setPositions(res.data || []);
    } catch (e) {
      console.error('Error fetching positions:', e);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setFormError('');
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (pos) => {
    setFormError('');
    setForm({
      id: pos.id,
      title: pos.title || '',
      job_description: pos.job_description || '',
      duties: pos.duties || '',
      expectations: pos.expectations || '',
      requirements: pos.requirements || '',
      pay_type: 'hourly',
      pay_min: String(pos.pay_min ?? ''),
      pay_max: String(pos.pay_max ?? ''),
    });
    setShowForm(true);
  };

  const openView = async (pos) => {
    setViewingPosition(pos);
    setShowEmail(false);
    setEmailTo('');

    // Fetch as blob so auth header is included (iframe cannot send Authorization)
    try {
      setViewPdfUrl('');
      const res = await axios.get(`${API}/hr/positions/${pos.id}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      setViewPdfUrl(blobUrl);
    } catch (e) {
      console.error('Failed to load PDF:', e);
      alert(e.response?.data?.detail || e.message);
      setViewingPosition(null);
    }
  };

  const closeView = () => {
    try {
      if (viewPdfUrl && viewPdfUrl.startsWith('blob:')) URL.revokeObjectURL(viewPdfUrl);
    } catch (e) {}

    setViewingPosition(null);
    setViewPdfUrl('');
    setShowEmail(false);
    setEmailTo('');
    setSendingEmail(false);
  };

  const handlePrint = async () => {
    if (!viewPdfUrl) return;
    try {
      const w = window.open(viewPdfUrl, '_blank');
      if (!w) return;
      w.focus();
      setTimeout(() => {
        try { w.print(); } catch (e) {}
      }, 700);
    } catch (e) {
      console.error('Print failed:', e);
    }
  };

  const handleSendEmail = async () => {
    if (!viewingPosition?.id) return;
    if (!emailTo.trim()) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/hr/positions/${viewingPosition.id}/send`, { recipient_email: emailTo.trim() });
      alert('Email sent.');
      setShowEmail(false);
      setEmailTo('');
    } catch (e) {
      console.error('Send email failed:', e);
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.title.trim()) return setFormError('Title is required.');
    if (!form.job_description.trim()) return setFormError('Job description is required.');
    if (!form.duties.trim()) return setFormError('Duties are required.');
    if (!form.expectations.trim()) return setFormError('Expectations are required.');
    if (!form.requirements.trim()) return setFormError('Requirements are required.');

    const payMin = Number(form.pay_min);
    const payMax = Number(form.pay_max);
    if (Number.isNaN(payMin) || Number.isNaN(payMax)) return setFormError('Pay range must be numeric.');
    if (payMin > payMax) return setFormError('Pay min must be ≤ pay max.');

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        job_description: form.job_description.trim(),
        duties: form.duties.trim(),
        expectations: form.expectations.trim(),
        requirements: form.requirements.trim(),
        pay_type: form.pay_type,
        pay_min: payMin,
        pay_max: payMax,
      };

      if (form.id) {
        await axios.put(`${API}/hr/positions/${form.id}`, payload);
      } else {
        await axios.post(`${API}/hr/positions`, payload);
      }

      setShowForm(false);
      await fetchPositions();
    } catch (err) {
      console.error('Error saving position:', err);
      setFormError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pos) => {
    if (!window.confirm(`Delete position "${pos.title}"?`)) return;
    try {
      await axios.delete(`${API}/hr/positions/${pos.id}`);
      await fetchPositions();
    } catch (err) {
      console.error('Error deleting position:', err);
      alert(err.response?.data?.detail || err.message);
    }
  };

  const filtered = positions.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.title || '').toLowerCase().includes(q) ||
      (p.job_description || '').toLowerCase().includes(q) ||
      (p.duties || '').toLowerCase().includes(q)
    );
  });

  const formatPay = (p) => {
    const min = Number(p.pay_min);
    const max = Number(p.pay_max);
    const label = p.pay_type === 'salary' ? 'Salary' : 'Hourly';
    if (p.pay_type === 'salary') {
      return `${label}: $${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    return `${label}: $${min.toFixed(2)} - $${max.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Back to Human Resources
          </button>

          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium"
          >
            + Add Position
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-purple-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">🧩 HR Positions</h1>
                <p className="text-gray-600 text-sm mt-1">Canonical list of job positions (used by Training assignments later).</p>
              </div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search positions..."
                className="w-full md:w-80 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-gray-600">Loading positions...</div>
            ) : filtered.length === 0 ? (
              <div className="text-gray-600">No positions found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-3 pr-4">Title</th>
                      <th className="py-3 pr-4">Pay Range</th>
                      <th className="py-3 pr-4">Summary</th>
                      <th className="py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} className="border-b last:border-b-0">
                        <td className="py-3 pr-4 font-medium text-gray-900">{p.title}</td>
                        <td className="py-3 pr-4 text-gray-700">{formatPay(p)}</td>
                        <td className="py-3 pr-4 text-gray-700">
                          <div className="line-clamp-2 max-w-xl">{p.job_description}</div>
                        </td>
                        <td className="py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => openView(p)}
                              className="px-3 py-1 rounded-md bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              View
                            </button>
                            <button
                              onClick={() => openEdit(p)}
                              className="px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(p)}
                              className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-800">{form.id ? 'Edit Position' : 'Add Position'}</h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-5">
                {formError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      placeholder="e.g., Foreman"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type *</label>
                    <select
                      value={form.pay_type}
                      onChange={(e) => setForm({ ...form, pay_type: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="salary">Salary</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pay Min *</label>
                      <input
                        value={form.pay_min}
                        onChange={(e) => setForm({ ...form, pay_min: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder={form.pay_type === 'salary' ? '65000' : '26'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pay Max *</label>
                      <input
                        value={form.pay_max}
                        onChange={(e) => setForm({ ...form, pay_max: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder={form.pay_type === 'salary' ? '80000' : '32'}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
                    <textarea
                      value={form.job_description}
                      onChange={(e) => setForm({ ...form, job_description: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      rows={3}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duties *</label>
                    <textarea
                      value={form.duties}
                      onChange={(e) => setForm({ ...form, duties: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      rows={3}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expectations *</label>
                    <textarea
                      value={form.expectations}
                      onChange={(e) => setForm({ ...form, expectations: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      rows={3}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requirements *</label>
                    <textarea
                      value={form.requirements}
                      onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-400"
                  >
                    {saving ? 'Saving...' : 'Save Position'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {viewingPosition && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{viewingPosition.title}</h2>
                  <p className="text-xs text-gray-500">PDF preview</p>
                </div>
                <button onClick={closeView} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button>
              </div>

              <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 rounded-md bg-gray-900 hover:bg-black text-white text-sm font-medium"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => setShowEmail((v) => !v)}
                    className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium"
                  >
                    Email
                  </button>
                </div>

                {showEmail && (
                  <div className="flex items-center gap-2">
                    <input
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="Recipient email"
                      className="w-72 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                    <button
                      onClick={handleSendEmail}
                      disabled={!emailTo.trim() || sendingEmail}
                      className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium"
                    >
                      {sendingEmail ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-gray-100 h-[76vh]">
                {viewPdfUrl ? (
                  <iframe
                    title="Position PDF"
                    src={viewPdfUrl}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-600">Loading PDF...</div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );


};

// Vendors Component

const HumanResourcesHomePage = ({ onNavigate, onBackToGears, currentUser }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Back to Gears Button */}
        <div className="mb-6">
          <button 
            onClick={onBackToGears}

            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Back to Company Gears
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center mb-6">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
              👥 HUMAN RESOURCES
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-gray-600 mb-2">Personnel Management & HR Operations</p>
          <p className="text-gray-500">Employee Administration & Human Resources Management</p>
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          
          {/* Employees Module */}
          <div 
            className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-purple-200"
            onClick={() => onNavigate('employees')}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">👷</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">EMPLOYEES</h2>
              <p className="text-gray-600 text-sm">Manage staff records, roles & contact information</p>
            </div>
          </div>

          {/* Positions Module */}
          {currentUser?.rawRole === 'admin' ? (
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-purple-200"
              onClick={() => onNavigate('hr-positions')}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🧩</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">POSITIONS</h2>
                <p className="text-gray-600 text-sm">Create and manage company job positions (used by Training)</p>
                <p className="text-purple-600 text-xs mt-2">🔒 Admin Only</p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
              <div className="text-center">
                <div className="text-6xl mb-4">🧩</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-600">POSITIONS</h2>
                <p className="text-gray-500 text-sm">Admin Only - Company job positions</p>
              </div>
            </div>
          )}

          {/* Future HR Modules - Coming Soon */}
          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">📄</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">PAYROLL</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Payroll management & processing</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">⏱️</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">TIME TRACKING</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Employee time & attendance</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">PERFORMANCE</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Employee performance reviews</p>
            </div>
          </div>

          <div 
            className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-emerald-200"
            onClick={onBackToGears}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🎓</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">TRAINING</h2>
              <p className="text-gray-600 text-sm">Use the new Training gear on the Gears page.</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">🏥</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">BENEFITS</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Employee benefits management</p>
            </div>
          </div>

        </div>

        {/* System Status Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            🟢 Human Resources Systems Operational | Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
        </div>
      </div>
  );
};

// ==================== TRAINING MODULE ====================


export { HumanResourcesHomePage, HREmployeesModule, HRPositionsModule };
